// Topbar collaboration cluster: the live "who's on this page" avatars (now clickable — nudge, message
// or start a co-op session), a chat button with an unread badge, and a co-op on/off toggle.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePresence } from '../features/hooks.js';
import { Avatar } from '../components/Avatar.js';
import { InlineError } from '../components/ui.js';
import { IconChat, IconCursor, IconNudge } from '../components/icons.js';
import { useCollab } from './CollabContext.js';
import { useIsCompact } from '../lib/media.js';

export function CollabDock() {
  const { pathname } = useLocation();
  const { data } = usePresence(pathname);
  const collab = useCollab();
  // the compact bar keeps three faces (+n); phones hide the stack and list everyone in the Account sheet
  const maxShown = useIsCompact() ? 3 : 5;
  const viewers = (data?.viewers ?? []).filter((v) => v.uid);
  const shown = viewers.slice(0, maxShown);
  const extra = viewers.length - shown.length;

  const [menuFor, setMenuFor] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuFor) return;
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuFor(null); };
    // Escape closes the action menu and puts focus back on the avatar that opened it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      wrapRef.current?.querySelector<HTMLButtonElement>('.collab-av[aria-expanded="true"]')?.focus();
      setMenuFor(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [menuFor]);

  return (
    <div className="collab-dock" ref={wrapRef} style={{ position: 'relative' }}>
      {shown.length > 0 && (
        <div className="collab-presence" title={`Also here: ${viewers.map((v) => v.name).join(', ')}`}>
          {shown.map((v) => (
            <div key={v.uid} className="collab-av-wrap">
              <button
                className="collab-av"
                onClick={() => setMenuFor((m) => (m === v.uid ? null : v.uid!))}
                title={v.name}
                aria-label={v.name}
                aria-expanded={menuFor === v.uid}
              >
                <Avatar name={v.name} avatar={v.avatar} size={26} />
              </button>
              {menuFor === v.uid && (
                <div className="collab-menu">
                  <div className="collab-menu-hd">{v.name}{v.department ? <span className="muted"> · {v.department}</span> : null}</div>
                  <button onClick={() => { collab.nudge(v.uid!, 'poke'); setMenuFor(null); }}><IconNudge size={15} /> Nudge</button>
                  <button onClick={() => { collab.openDmWith(v.uid!); setMenuFor(null); }}><IconChat size={15} /> Message</button>
                  <button onClick={() => { collab.startCoopWith(v.uid!); setMenuFor(null); }}><IconCursor size={15} /> Start co-op</button>
                </div>
              )}
            </div>
          ))}
          {extra > 0 && <span className="collab-more">+{extra}</span>}
        </div>
      )}

      {/* the co-op toggle: phones move it into the Account sheet as a labelled switch */}
      <button
        className={`collab-btn collab-coop ${collab.coopOn ? 'on' : ''}`}
        title={collab.coopOn ? 'Co-op on. Click to leave.' : 'Co-op: join whoever is in co-op on this page, or start a session (invite someone from their picture)'}
        aria-label={collab.coopPeers.length ? `Co-op, ${collab.coopPeers.length} ${collab.coopPeers.length === 1 ? 'other' : 'others'}` : 'Co-op'}
        aria-pressed={collab.coopOn}
        onClick={collab.toggleCoop}
      >
        <IconCursor size={17} />{collab.coopPeers.length > 0 && <span className="collab-badge">{collab.coopPeers.length}</span>}
      </button>

      <button
        className="collab-btn"
        title="Messages"
        aria-label={collab.unread > 0 ? `Messages, ${collab.unread} unread` : 'Messages'}
        onClick={() => collab.openChat()}
      >
        <IconChat size={17} />{collab.unread > 0 && <span className="collab-badge unread">{collab.unread > 99 ? '99+' : collab.unread}</span>}
      </button>

      {/* the menu closes on click, so a nudge / co-op invite that fails to send reports here, under the
          dock (stepping aside while an avatar menu is open, since both drop into the same spot). */}
      {collab.nudgeError && !menuFor && (
        <div className="help-pop" style={{ width: 'min(280px, 86vw)', paddingBottom: 4 }}>
          <div className="help-pop-hd">{collab.nudgeError.kind === 'coop' ? 'Co-op invite not sent' : 'Nudge not sent'}</div>
          <InlineError error={collab.nudgeError.error} onDismiss={collab.clearNudgeError} />
        </div>
      )}
    </div>
  );
}
